import { ReactNode, useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import Switch from 'react-switch'

import Header from 'components/headers/bulletins'
import BaleIcon from 'assets/images/notifications/bale.svg'
import EitaaIcon from 'assets/images/notifications/eitaa.svg'
import SmsIcon from 'assets/images/notifications/sms.svg'
import HamyanIcon from 'assets/images/notifications/hamyan-web.png'
import {
    getMessagingChannels,
    updateMessagingChannel,
} from 'services/messagingChannels'
import { getWebPushConfig } from 'services/webPush'
import {
    disableWebPush,
    EnableStage,
    enableWebPush,
    isWebPushOn,
    notificationPermission,
    pushAvailability,
} from 'modules/webPush'
import { queueEvent } from 'utilities/analytics'
import WebPushPermission from './permission'
import WebPushSelfTest from './selfTest'
import { switchProps } from './switchProps'
import TestSend from './testSend'
import TopicsSection from './topics'

const CONFIG_KEY = ['web-push-config']
const CHANNELS_KEY = ['messaging-channels']

// Said under the switch while a tap is in flight: turning on can take half a
// minute on a browser's first visit, and a greyed-out switch alone reads as
// «nothing happens».
const STAGE_TEXT: Record<EnableStage, string> = {
    permission:
        'مرورگر برای اعلان اجازه می‌خواهد؛ «Allow» یا «اجازه» را بزنید. اگر پنجره‌ای نمی‌بینید، روی آیکون کنار نشانی سایت بزنید.',
    preparing:
        'در حال روشن کردن اعلان روی این مرورگر… بار اول ممکن است تا نیم دقیقه طول بکشد.',
}

const CHANNELS: Record<TDirectChannel, { title: string; icon: string }> = {
    bale: { title: 'بله', icon: BaleIcon },
    eitaa: { title: 'ایتا', icon: EitaaIcon },
    sms: { title: 'پیامک', icon: SmsIcon },
}

/** Why this browser cannot be switched on, in words a member can act on. */
const blockedReason = (
    availability: ReturnType<typeof pushAvailability>,
    serverEnabled: boolean | undefined
): string | null => {
    if (availability === 'miniapp')
        return 'داخل بله، تلگرام و ایتا اعلان وب نمی‌رسد. برای دریافت اعلان، همیان را در مرورگر گوشی باز کنید: pwa.hamyanapp.com'
    if (availability === 'ios_browser')
        return 'در آیفون و آیپد، اعلان فقط وقتی می‌رسد که همیان روی صفحهٔ اصلی باشد: در Safari دکمهٔ اشتراک‌گذاری و بعد «Add to Home Screen» را بزنید و همیان را از همان آیکون باز کنید.'
    if (availability === 'unsupported')
        return 'این مرورگر اعلان وب را پشتیبانی نمی‌کند. نسخهٔ تازهٔ Chrome، Edge یا Firefox را امتحان کنید.'
    // `permission === 'denied'` is not here on purpose: the «دسترسی اعلان در
    // مرورگر» card below says it with the steps for this exact browser.
    if (serverEnabled === false) return 'اعلان وب همیان موقتاً در دسترس نیست.'
    return null
}

/**
 * What this channel is doing for the member right now, in one line.
 *
 * `enabled` is their switch and `active` is ours: only one channel ever
 * carries a message, and a switch that is on over a messenger we cannot reach
 * still means an SMS. Saying just «روشن» would let the green imply otherwise.
 */
const channelHint = (
    channel: IMessengerChannel,
    title: string,
    primary: string | null
): string => {
    // The SMS reads differently from a messenger on every line: there is
    // nothing to «open» and nothing to go stale, and it is the only one the
    // fund pays for. Its own copy rather than a messenger sentence with
    // «پیامک» substituted into it.
    if (channel.key === 'sms') {
        if (!channel.enabled) return 'خاموش است؛ دیگر برایتان پیامک نمی‌فرستیم.'
        if (!channel.active) return 'شماره‌ای از شما نداریم که پیامک بفرستیم.'
        if (primary)
            return `فقط وقتی خبری در ${primary} به دستتان نرسد، پیامک می‌فرستیم.`
        return 'خبرهای همیان پیامکی برایتان می‌آید؛ هزینه‌اش با صندوق است.'
    }
    if (!channel.enabled) return `خاموش است؛ خبرها از ${title} برایتان نمی‌آید.`
    if (!channel.linked)
        return `همیان را داخل ${title} باز کنید تا خبرها همان‌جا برسد.`
    if (!channel.active)
        return `مدتی است همیان را در ${title} باز نکرده‌اید؛ تا باز نکنید خبرها پیامکی می‌رسد.`
    if (primary && primary !== title)
        return `اگر پیام در ${primary} نرسد، خبرها را اینجا می‌فرستیم.`
    return 'خبرهای همیان همین‌جا برایتان می‌آید.'
}

function ChannelRow({
    icon,
    title,
    hint,
    checked,
    disabled,
    onChange,
    children,
}: {
    icon: string
    title: string
    hint: string
    checked: boolean
    disabled?: boolean
    onChange: () => void
    children?: ReactNode
}) {
    return (
        <div className="py-3 border-b border-divider last:border-b-0">
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <img
                        src={icon}
                        alt=""
                        aria-hidden="true"
                        className="w-9 h-9 shrink-0"
                    />
                    <div className="min-w-0">
                        <p className="text-sm font-bold">{title}</p>
                        <p className="text-xs text-text-secondary mt-1 leading-5">
                            {hint}
                        </p>
                    </div>
                </div>
                <Switch
                    {...switchProps}
                    onChange={onChange}
                    checked={checked}
                    disabled={disabled}
                    aria-label={title}
                />
            </div>
            {children}
        </div>
    )
}

export default function NotificationSettings() {
    const queryClient = useQueryClient()
    const availability = pushAvailability()
    const [permission, setPermission] = useState(notificationPermission())
    const [deviceOn, setDeviceOn] = useState(isWebPushOn())
    const [busy, setBusy] = useState(false)
    const [stage, setStage] = useState<EnableStage | null>(null)
    const viewedTracked = useRef(false)

    const {
        data: config,
        isLoading,
        isError,
        refetch,
    } = useQuery(CONFIG_KEY, getWebPushConfig, { staleTime: 60_000, retry: 1 })

    const { data: channelsData } = useQuery(
        CHANNELS_KEY,
        getMessagingChannels,
        { staleTime: 60_000, retry: 1 }
    )

    // The messenger rows and the SMS row are shown together but answer to
    // different things: the kill switch hides the messengers — they decide
    // nothing while it is off — and leaves the SMS, which in that state is the
    // only switch on this page that still does something.
    const messengers =
        channelsData?.messengers_enabled === false
            ? []
            : (channelsData?.channels ?? []).filter((row) => row.key !== 'sms')
    const sms = channelsData?.channels.find((row) => row.key === 'sms') ?? null

    // The one that would actually carry a message: the first switched-on
    // messenger we can still reach. The others say what they are — a fallback,
    // or a reason the SMS is still coming.
    const primary = messengers.find((row) => row.enabled && row.active) ?? null
    const primaryTitle = primary ? CHANNELS[primary.key].title : null
    // Nothing free is left, so the fund pays for a text. Worth saying out loud.
    const fallsBackToSms =
        messengers.length > 0 &&
        messengers.every((row) => !row.enabled) &&
        !!sms?.enabled
    // Both halves of the direct lane are off. Not a mistake — a member is
    // allowed to want this — but they should hear it from us rather than from
    // the silence, and hear that the app itself still tells them everything.
    const directLaneSilent = !primary && !!sms && !sms.enabled
    // Web push answers to the person, not to this browser, and it does not
    // belong to the direct lane at all: a switch left on anywhere still carries
    // the news. So the silence warning has to look at it before claiming there
    // is nothing left — otherwise we tell a member who gets browser
    // notifications that they get nothing.
    const webReaches =
        !!config?.enabled && (deviceOn || (config?.active_devices ?? 0) > 0)

    useEffect(() => {
        if (viewedTracked.current || isLoading) return
        viewedTracked.current = true
        queueEvent('web_push_settings_viewed', {
            availability,
            permission,
            device_on: deviceOn,
            active_devices: config?.active_devices ?? null,
        })
        // Once, when the first answer is in.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading])

    const blocked = blockedReason(availability, config?.enabled)

    const toggleChannel = async (channel: IMessengerChannel) => {
        if (!channelsData) return
        const enabled = !channel.enabled
        const previous = channelsData
        queryClient.setQueryData<IMessengerChannelsResponse>(CHANNELS_KEY, {
            ...channelsData,
            channels: channelsData.channels.map((row) =>
                row.key === channel.key ? { ...row, enabled } : row
            ),
        })
        try {
            // The answer is the whole list: one switch changes what the others
            // mean, and only the backend knows which one is carrying now.
            queryClient.setQueryData(
                CHANNELS_KEY,
                await updateMessagingChannel(channel.key, enabled)
            )
            queueEvent('notification_channel_toggled', {
                channel: channel.key,
                enabled,
                linked: channel.linked,
                active: channel.active,
            })
        } catch {
            queryClient.setQueryData(CHANNELS_KEY, previous)
            toast.error('تغییر ذخیره نشد. دوباره امتحان کنید.')
        }
    }

    const toggleDevice = async () => {
        if (busy) return
        setBusy(true)
        try {
            if (deviceOn) {
                const ok = await disableWebPush()
                if (ok) {
                    setDeviceOn(false)
                    toast.info('اعلان‌ها روی این دستگاه خاموش شد.')
                } else {
                    toast.error(
                        'خاموش کردن اعلان‌ها انجام نشد. دوباره امتحان کنید.'
                    )
                }
            } else {
                const result = await enableWebPush(
                    'settings',
                    setStage,
                    config ?? null
                )
                setPermission(notificationPermission())
                if (result === 'subscribed') {
                    setDeviceOn(true)
                    toast.success('اعلان‌ها روی این دستگاه روشن شد.')
                } else if (result === 'failed') {
                    toast.error(
                        'روشن کردن اعلان‌ها انجام نشد. کمی بعد دوباره امتحان کنید.'
                    )
                }
                // denied / dismissed / unavailable explain themselves on the page.
            }
        } finally {
            // Whatever happened, the switch must not stay stuck grey.
            setBusy(false)
            setStage(null)
            queryClient.invalidateQueries(CONFIG_KEY)
        }
    }

    return (
        <div className="flex flex-col bg-canvas min-h-screen">
            <Header title="مدیریت اعلان‌ها" />

            <div className="px-4 py-4 flex flex-col gap-4">
                <section className="bg-white rounded-lg shadow-header p-4">
                    <p className="text-base font-bold">کجا خبرتان کنیم؟</p>
                    <p className="text-xs text-text-secondary mt-1 leading-6">
                        همان کارهای مهمی که اپ همیان خبر می‌دهد، هر جا که برای
                        شما روشن است به دستتان می‌رسد.
                    </p>

                    <div className="mt-2">
                        {messengers.map((channel) => (
                            <ChannelRow
                                key={channel.key}
                                icon={CHANNELS[channel.key].icon}
                                title={CHANNELS[channel.key].title}
                                hint={channelHint(
                                    channel,
                                    CHANNELS[channel.key].title,
                                    primaryTitle
                                )}
                                checked={channel.enabled}
                                onChange={() => toggleChannel(channel)}
                            />
                        ))}

                        {sms && (
                            <ChannelRow
                                icon={CHANNELS.sms.icon}
                                title={CHANNELS.sms.title}
                                hint={channelHint(
                                    sms,
                                    CHANNELS.sms.title,
                                    primaryTitle
                                )}
                                checked={sms.enabled}
                                onChange={() => toggleChannel(sms)}
                            />
                        )}

                        <ChannelRow
                            icon={HamyanIcon}
                            title="وب همیان"
                            hint="اعلان مرورگر روی همین دستگاه."
                            checked={deviceOn}
                            disabled={
                                busy ||
                                isLoading ||
                                (!deviceOn &&
                                    (!!blocked || permission === 'denied'))
                            }
                            onChange={toggleDevice}
                        >
                            {busy && stage && (
                                <p
                                    className="mt-3 text-xs leading-6 rounded-md bg-canvas p-3"
                                    aria-live="polite"
                                >
                                    {STAGE_TEXT[stage]}
                                </p>
                            )}
                            {blocked && !deviceOn && (
                                <p className="mt-3 text-xs leading-6 rounded-md bg-warning-soft text-warning-on p-3">
                                    {blocked}
                                </p>
                            )}
                            {isError && (
                                <button
                                    type="button"
                                    onClick={() => refetch()}
                                    className="mt-3 text-xs text-brand-deep underline"
                                >
                                    تنظیمات بارگیری نشد؛ دوباره امتحان کنید
                                </button>
                            )}
                        </ChannelRow>
                    </div>

                    {messengers.length > 0 && (
                        <p className="mt-3 text-xs text-text-secondary leading-6">
                            یک خبر را در بله و ایتا با هم نمی‌فرستیم؛ اگر هر دو
                            روشن باشد، پیام در بله می‌آید و اگر نرسید، در ایتا.
                        </p>
                    )}
                    <TestSend />
                    {fallsBackToSms && (
                        <p className="mt-2 text-xs leading-6 rounded-md bg-warning-soft text-warning-on p-3">
                            حالا که هر دو پیام‌رسان خاموش است، خبرها دوباره
                            پیامکی می‌رسد و هزینهٔ پیامک از حساب صندوق کم
                            می‌شود.
                        </p>
                    )}
                    {directLaneSilent && !webReaches && (
                        <p className="mt-2 text-xs leading-6 rounded-md bg-warning-soft text-warning-on p-3">
                            پیامک هم خاموش است، پس هیچ خبری بیرون از همیان به
                            شما نمی‌رسد. خبرها همچنان در خود اپ و در «پیام‌ها»
                            هست؛ برای دیدنشان باید خودتان سر بزنید.
                        </p>
                    )}
                    {directLaneSilent && webReaches && (
                        <p className="mt-2 text-xs leading-6 rounded-md bg-warning-soft text-warning-on p-3">
                            پیامک هم خاموش است؛ تنها راهی که برایتان مانده «وب
                            همیان» است. اگر آن را هم خاموش کنید، هیچ خبری بیرون
                            از همیان نمی‌رسد و باید خودتان در اپ و «پیام‌ها» سر
                            بزنید.
                        </p>
                    )}
                </section>

                {availability === 'ok' && (
                    <WebPushPermission
                        permission={permission}
                        busy={busy}
                        onAllow={toggleDevice}
                    />
                )}

                {/* The five browser-only category switches that used to be
                    here are «چه خبرهایی بفرستیم» now: one list, per fund,
                    for every channel at once. */}
                <TopicsSection />

                {deviceOn && config?.enabled && <WebPushSelfTest />}

                <p className="text-xs text-text-secondary leading-6 px-1">
                    برای اینکه مزاحم نشویم: از ساعت ۲۲ تا ۸ صبح فقط رسید
                    پرداخت‌ها، آن هم بی‌صدا، می‌رسد و بقیه تا صبح صبر می‌کنند.
                    از هر نوع اعلان هم در روز تعداد محدودی می‌فرستیم و یک خبر را
                    دو بار نمی‌فرستیم.
                </p>
            </div>
        </div>
    )
}
